import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Film, Loader2, ArrowLeft, ImagePlus, X, Mic, Music, Wand2, Upload } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { NeonBackground } from "../components/NeonBackground";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { api } from "../lib/api";
import { VideoCard } from "../components/VideoCard";

const DURATIONS = [5, 10, 30];
const RATIOS = [{ key: "9:16", label: "Verticale 9:16" }, { key: "16:9", label: "Orizzontale 16:9" }, { key: "1:1", label: "Quadrato 1:1" }];
const ACCENTS = [{ key: "cyan", label: "Ciano", c: "#00F0FF" }, { key: "pink", label: "Rosa", c: "#FF2E97" }, { key: "green", label: "Verde", c: "#39FF14" }];
const AI_STYLES = [{ key: "realistic", label: "Realistico" }, { key: "pixar", label: "Pixar 3D" }, { key: "cartoon", label: "Cartoon" }, { key: "fantasy", label: "Fantasy" }, { key: "neon", label: "Neon" }];
const AI_IMAGES = { 5: 3, 10: 4, 30: 6 };

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`px-3 py-2 rounded-sm text-sm border transition-colors ${active ? "bg-neon-cyan text-black border-neon-cyan" : "bg-input text-white border-white/15 hover:border-neon-cyan/50"}`}>
    {children}
  </button>
);

const Label = ({ children }) => <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{children}</label>;

const contactsFrom = (c = {}) => {
  const parts = [c.whatsapp && `WhatsApp ${c.whatsapp}`, c.instagram && `@${String(c.instagram).replace(/^@/, "")}`].filter(Boolean);
  if (!parts.length && c.phone) parts.push(`Tel. ${c.phone}`);
  if (!parts.length && c.email) parts.push(c.email);
  return parts.join(" · ");
};

export default function VideoAdPage() {
  const [mode, setMode] = useState("photos");
  const [clips, setClips] = useState([]);
  const [clipProgress, setClipProgress] = useState(null);
  const [audioMode, setAudioMode] = useState("keep");
  const [scene, setScene] = useState("");
  const [aiStyle, setAiStyle] = useState("realistic");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contacts, setContacts] = useState("");
  const [duration, setDuration] = useState(10);
  const [ratio, setRatio] = useState("9:16");
  const [accent, setAccent] = useState("cyan");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [withVoice, setWithVoice] = useState(false);
  const [narration, setNarration] = useState("");
  const [music, setMusic] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [videos, setVideos] = useState([]);
  const timer = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/videos");
      setVideos(r.data);
    } catch {}
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setContacts(contactsFrom(r.data?.contacts))).catch(() => {});
    return () => { clearInterval(timer.current); };
  }, []);

  useEffect(() => {
    clearInterval(timer.current);
    if (videos.some((v) => v.status === "processing")) {
      timer.current = setInterval(load, 3000);
    }
  }, [videos]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const up = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        up.push({ path: r.data.path, preview: URL.createObjectURL(f) });
      }
      setPhotos((p) => [...p, ...up]);
    } catch {
      toast.error("Caricamento foto non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const onClips = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - clips.length);
    e.target.value = "";
    for (const f of files) {
      if (f.size > 60 * 1024 * 1024) { toast.error(`${f.name}: file troppo grande (max 60 MB)`); continue; }
      try {
        setClipProgress(0);
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
          onUploadProgress: (p) => setClipProgress(Math.round((p.loaded / (p.total || f.size)) * 100)),
        });
        setClips((c) => [...c, { path: r.data.path, name: f.name, size: f.size }]);
      } catch {
        toast.error(`Caricamento di ${f.name} non riuscito`);
      }
    }
    setClipProgress(null);
  };

  const generate = async () => {
    if (mode === "clips" && !clips.length) { toast.error("Carica almeno un video creato con Gemini"); return; }
    if (mode === "photos" && !title.trim()) { toast.error("Scrivi un titolo per il video"); return; }
    if (mode === "ai" && !scene.trim()) { toast.error("Descrivi la scena che l'AI deve disegnare"); return; }
    if (withVoice && (mode !== "clips" || audioMode === "replace") && !narration.trim()) { toast.error("Scrivi il testo della voce narrante"); return; }
    setSubmitting(true);
    try {
      await api.post("/videos", {
        title, mode, scene_prompt: scene, ai_style: aiStyle, description, contacts, photos: mode === "photos" ? photos.map((p) => p.path) : [], clips: mode === "clips" ? clips.map((c) => c.path) : [], audio_mode: audioMode, duration,
        aspect_ratio: ratio, accent, narration: withVoice && (mode !== "clips" || audioMode === "replace") ? narration : null, music: mode === "clips" ? audioMode !== "keep" && music : music,
      });
      toast.success(mode === "ai" ? "L'AI sta disegnando le scene: pronto in circa 1 minuto." : "Video in creazione! Pronto in pochi secondi.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Creazione non riuscita");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo video?")) return;
    try {
      await api.delete(`/videos/${id}`);
      setVideos((v) => v.filter((x) => x.id !== id));
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const words = narration.trim() ? narration.trim().split(/\s+/).length : 0;
  const maxWords = Math.round(duration * 2.2);

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <NeonBackground />
      <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <p className="font-mono text-xs uppercase tracking-widest text-neon-cyan flex items-center gap-2"><Film className="w-4 h-4" /> Solo amministratore · Foto e video Gemini gratis · Scene AI a pochi centesimi</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-white mt-2 mb-8">Video Pubblicitario</h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3 space-y-6 bg-card border border-white/10 rounded-sm p-6">
            <div>
              <Label>Modalità</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                <Chip testid="video-mode-photos" active={mode === "photos"} onClick={() => setMode("photos")}>Con le mie foto · Gratis</Chip>
                <Chip testid="video-mode-ai" active={mode === "ai"} onClick={() => setMode("ai")}>Scene AI</Chip>
                <Chip testid="video-mode-clips" active={mode === "clips"} onClick={() => setMode("clips")}>Carica video Gemini · Gratis</Chip>
              </div>
            </div>

            {mode === "clips" && (
              <div className="space-y-4">
                <div>
                  <Label>Video creati con Gemini / Veo (fino a 4)</Label>
                  <p className="text-[11px] text-muted-foreground font-light mt-0.5">Crea il video nell'app Gemini col tuo abbonamento, scaricalo e caricalo qui. Più clip vengono unite in ordine. Aggiungo logo, titolo e finale con i contatti.</p>
                  <div className="space-y-2 mt-2">
                    {clips.map((c, i) => (
                      <div key={i} data-testid={`video-clip-${i}`} className="flex items-center justify-between bg-input border border-white/10 rounded-sm px-3 py-2 text-sm">
                        <span className="truncate text-white">{i + 1}. {c.name} <span className="text-muted-foreground text-xs">({(c.size / 1048576).toFixed(1)} MB)</span></span>
                        <button data-testid={`video-clip-remove-${i}`} onClick={() => setClips(clips.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-neon-pink"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    {clips.length < 4 && (
                      <label data-testid="video-clip-add" className="flex items-center justify-center gap-2 border border-dashed border-white/25 rounded-sm py-4 cursor-pointer hover:border-neon-cyan/50 text-sm text-muted-foreground transition-colors">
                        {clipProgress !== null ? <><Loader2 className="w-4 h-4 animate-spin text-neon-cyan" /> Caricamento {clipProgress}%</> : <><Upload className="w-4 h-4" /> Carica video</>}
                        <input data-testid="video-clip-input" type="file" accept="video/*" multiple className="hidden" onChange={onClips} disabled={clipProgress !== null} />
                      </label>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Audio originale del video</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Chip testid="video-audio-keep" active={audioMode === "keep"} onClick={() => setAudioMode("keep")}>Tieni originale</Chip>
                    <Chip testid="video-audio-keep_music" active={audioMode === "keep_music"} onClick={() => setAudioMode("keep_music")}>Originale + musica</Chip>
                    <Chip testid="video-audio-replace" active={audioMode === "replace"} onClick={() => setAudioMode("replace")}>Sostituisci con musica/voce</Chip>
                  </div>
                </div>
              </div>
            )}

            {mode === "ai" ? (
              <div className="space-y-4">
                <div>
                  <Label>Descrivi la scena</Label>
                  <Textarea data-testid="video-scene" rows={3} value={scene} onChange={(e) => setScene(e.target.value)}
                    placeholder="Es. Un elfo nel bosco che parla e mostra un portachiavi stampato in 3D"
                    className="bg-input border-white/10 rounded-sm mt-1 resize-none" />
                  <p data-testid="video-ai-cost" className="text-[11px] text-muted-foreground font-light mt-1">L'AI disegna {AI_IMAGES[duration]} scene con lo stesso personaggio (circa {(AI_IMAGES[duration] * 0.04).toFixed(2).replace(".", ",")} $ di crediti), poi l'app le anima gratis. I personaggi non muovono la bocca: usa la voce narrante per farli "parlare".</p>
                </div>
                <div>
                  <Label>Stile</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {AI_STYLES.map((s) => <Chip key={s.key} testid={`video-style-${s.key}`} active={aiStyle === s.key} onClick={() => setAiStyle(s.key)}>{s.label}</Chip>)}
                  </div>
                </div>
              </div>
            ) : mode === "photos" ? (
            <div>
              <Label>Foto dei tuoi lavori (fino a 6)</Label>
              <p className="text-[11px] text-muted-foreground font-light mt-0.5">Le foto vengono animate con zoom e transizioni. Senza foto uso uno sfondo neon.</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-sm overflow-hidden border border-white/15">
                    <img src={p.preview} alt="foto" data-testid={`video-photo-thumb-${i}`} className="w-full h-full object-cover" />
                    <button data-testid={`video-photo-remove-${i}`} onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 p-0.5 hover:bg-neon-pink/80"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ))}
                {photos.length < 6 && (
                  <label data-testid="video-photo-add" className="w-16 h-16 rounded-sm border border-dashed border-white/25 flex items-center justify-center cursor-pointer hover:border-neon-cyan/50 transition-colors">
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin text-neon-cyan" /> : <ImagePlus className="w-5 h-5 text-muted-foreground" />}
                    <input data-testid="video-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
                  </label>
                )}
              </div>
            </div>
            ) : null}

            <div>
              <Label>{mode === "photos" ? "Titolo" : "Titolo (facoltativo)"}</Label>
              <Input data-testid="video-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Portachiavi personalizzati" className="bg-input border-white/10 rounded-sm mt-1" />
            </div>
            <div>
              <Label>Testo promozionale (facoltativo)</Label>
              <Textarea data-testid="video-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Es. Stampati in 3D con il tuo nome. -20% fino a domenica!" className="bg-input border-white/10 rounded-sm mt-1 resize-none" />
            </div>
            <div>
              <Label>Contatti nel finale</Label>
              <Input data-testid="video-contacts" value={contacts} onChange={(e) => setContacts(e.target.value)} placeholder="Es. WhatsApp 333 1234567 · @accreator3d" className="bg-input border-white/10 rounded-sm mt-1" />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label>Durata</Label>
                {mode === "clips" ? (
                  <p data-testid="video-clips-duration" className="text-sm text-muted-foreground font-light mt-2">Durata delle clip + 3s di finale</p>
                ) : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {DURATIONS.map((d) => <Chip key={d} testid={`video-duration-${d}`} active={duration === d} onClick={() => setDuration(d)}>{d}s</Chip>)}
                </div>
                )}
              </div>
              <div>
                <Label>Colore</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ACCENTS.map((a) => (
                    <Chip key={a.key} testid={`video-accent-${a.key}`} active={accent === a.key} onClick={() => setAccent(a.key)}>
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ background: a.c }} />{a.label}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label>Formato</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {RATIOS.map((r) => <Chip key={r.key} testid={`video-ratio-${r.key.replace(":", "x")}`} active={ratio === r.key} onClick={() => setRatio(r.key)}>{r.label}</Chip>)}
              </div>
              {mode === "clips" && <p className="text-[11px] text-muted-foreground font-light mt-1">Se il video caricato ha un formato diverso, viene ritagliato al centro.</p>}
            </div>

            {(mode !== "clips" || audioMode === "replace") && (
            <div className="border-t border-white/10 pt-5 space-y-4">
              <label className="flex items-center gap-2 text-sm text-white"><Switch data-testid="video-music-toggle" checked={music} onCheckedChange={setMusic} /> <Music className="w-4 h-4 text-neon-cyan" /> Musica di sottofondo</label>
              <label className="flex items-center gap-2 text-sm text-white"><Switch data-testid="video-voice-toggle" checked={withVoice} onCheckedChange={setWithVoice} /> <Mic className="w-4 h-4 text-neon-pink" /> Voce narrante in italiano</label>
              {withVoice && (
                <div>
                  <Label>Testo da leggere</Label>
                  <Textarea data-testid="video-narration" rows={3} value={narration} onChange={(e) => setNarration(e.target.value)}
                    placeholder="Es. Regala un pezzo unico! Portachiavi personalizzati AC Creator 3D. Scrivici ora!"
                    className="bg-input border-white/10 rounded-sm mt-1 resize-none" />
                  <p data-testid="video-word-count" className={`text-[11px] mt-1 ${words > maxWords ? "text-neon-pink" : "text-muted-foreground"}`}>{words} parole · consigliate max {maxWords} per {duration}s</p>
                </div>
              )}
            </div>
            )}

            <Button data-testid="video-generate" onClick={generate} disabled={submitting || uploading || clipProgress !== null || (mode === "ai" && videos.some((v) => v.mode === "ai" && v.status === "processing"))} className="w-full rounded-sm gap-2 bg-neon-cyan text-black hover:bg-white h-12 text-base font-semibold transition-colors">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />} Crea video
            </Button>
            <p className="text-xs text-muted-foreground font-light">Creato dall'app: gratis con le tue foto o i video Gemini, pochi centesimi con le Scene AI.</p>
          </motion.div>

          <div className="lg:col-span-2 space-y-4">
            <h2 className="font-display font-bold text-lg text-white">I tuoi video</h2>
            {videos.length === 0 && <div data-testid="video-empty" className="bg-card border border-white/10 rounded-sm p-8 text-center text-muted-foreground font-light"><Film className="w-8 h-8 mx-auto opacity-40 mb-2" />Nessun video ancora.</div>}
            {videos.map((v) => <VideoCard key={v.id} video={v} onDelete={remove} />)}
          </div>
        </div>
      </main>
    </div>
  );
}
